/**
 * The operational calendar for scheduling is Asia/Dubai — regardless of where
 * the operator's browser is.
 *
 * Storage convention (backend-wide): a "day" is encoded as the UTC date-part,
 * i.e. `YYYY-MM-DDT00:00:00.000Z`, where the date IS the Dubai operational day.
 * The API's driver lookup (checkForPlannedRoutes) and all analytics compare on
 * that date-part, so any value built from the browser's local clock or rendered
 * with local Date methods drifts a day for remote operators (USA -1, Maldives
 * +1 at midnight, etc.). Every scheduling picker and "today" seed must go
 * through these helpers instead of `new Date()` / `toDateString()`.
 */

const DUBAI_TZ = "Asia/Dubai";

/** Today's date in Dubai as "YYYY-MM-DD" (en-CA locale formats ISO-style). */
export function dubaiTodayStr() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: DUBAI_TZ }).format(new Date());
}

/** Dubai calendar day (YYYY-MM-DD) that a real instant (ISO string, Date or ms) falls in. */
export function dubaiDayStr(value) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: DUBAI_TZ }).format(new Date(value));
}

/** Canonical stored value for a day: midnight UTC of that calendar date. */
export function dayValue(dayStr) {
  return `${dayStr}T00:00:00.000Z`;
}

/** The day-bucket of any ISO datetime/date string: its date-part. */
export function datePart(iso) {
  return String(iso).slice(0, 10);
}

/** dayStr + n days, in pure UTC arithmetic (no local-timezone involvement). */
export function addDays(dayStr, n) {
  const d = new Date(`${dayStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Human label for a day string, rendered in UTC so the label always names the
 * day itself ("Wed Aug 05 2026"), never the browser's local rendering of it.
 */
export function dayLabel(dayStr, options) {
  return new Date(`${dayStr}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
    ...(options || {}),
  });
}

/**
 * The next `n` Dubai days as picker rows. Each row carries both naming
 * conventions used across the app:
 *   { day, label, value }  and aliases  { date: label, timestamp: value }
 */
export function dubaiDayList(n) {
  const rows = [];
  const today = dubaiTodayStr();
  for (let i = 0; i < n; i++) {
    const day = addDays(today, i);
    const label = dayLabel(day);
    const value = dayValue(day);
    rows.push({ day, label, value, date: label, timestamp: value });
  }
  return rows;
}

/**
 * Dubai's current date as a local-midnight Date object — for existing code
 * that walks/formats days with local Date arithmetic. Seeding those walks from
 * this keeps every derived day string on the Dubai calendar.
 */
export function dubaiTodayLocalDate() {
  return new Date(`${dubaiTodayStr()}T00:00:00`);
}
